import { useState, useRef, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function SkipChatV2({ embedded = false }) {
  const [displayMessages, setDisplayMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const [recruitId, setRecruitId] = useState(null);
  const recruitIdRef = useRef(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const initFiredRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayMessages, loading]);

  useEffect(() => {
    if (!loading && !complete && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, complete]);

  const fetchSkipReply = useCallback(async (outgoingMessages, currentRecruitId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/skip/v2/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: outgoingMessages, recruitId: currentRecruitId }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setApiMessages(data.messages);
      if (data.recruitId) {
        recruitIdRef.current = data.recruitId;
        setRecruitId(data.recruitId);
      }
      if (data.complete) {
        setComplete(true);
        setTimeout(() => {
          try {
            window.parent.postMessage({ type: 'skip-complete' }, '*');
          } catch {
            // standalone /skip-embed — not iframed, no-op
          }
        }, 4000);
      }

      const cleanReply = (data.reply || '').replace(/\*\*/g, '');
      setDisplayMessages(prev => [...prev, { role: 'assistant', content: cleanReply }]);
    } catch {
      setDisplayMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Yo, hiccup on my end — give it a sec and try again." },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initFiredRef.current) {
      initFiredRef.current = true;
      fetchSkipReply([], null);
    }
  }, [fetchSkipReply]);

  function handleSend() {
    if (!input.trim() || loading || complete) return;
    const text = input.trim();
    setInput('');
    const userMsg = { role: 'user', content: text };
    setDisplayMessages(prev => [...prev, userMsg]);
    fetchSkipReply([...apiMessages, userMsg], recruitIdRef.current);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className={`flex flex-col bg-white font-body overflow-hidden ${
      embedded
        ? 'w-full h-full'
        : 'w-[420px] h-[640px] rounded-[10px] shadow-[0_8px_40px_rgba(0,0,0,0.22)]'
    }`}>

      {/* Header */}
      <div className="bg-hunter-deep flex items-center px-[18px] py-[14px] flex-shrink-0">
        <div>
          <div className="text-white font-display font-black text-[17px] tracking-tight">Skip: Recruit Assistant</div>
          <div className="text-mid-green text-[10px] tracking-[0.22em] uppercase font-bold mt-0.5">
            CCF RECRUITING
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-[14px] flex flex-col gap-[9px] bg-[#F9FAFA]"
      >
        {displayMessages.map((msg, i) => {
          const isSkip = msg.role === 'assistant';
          return (
            <div key={i} className={`flex ${isSkip ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[80%] px-[13px] py-[9px] text-sm leading-[1.55] whitespace-pre-wrap break-words ${
                  isSkip
                    ? 'bg-[#EAECEC] text-[#1A1A1A] rounded-tl-[4px] rounded-tr-[14px] rounded-br-[14px] rounded-bl-[14px]'
                    : 'bg-hunter text-white rounded-tl-[14px] rounded-tr-[4px] rounded-br-[14px] rounded-bl-[14px]'
                }`}
              >
                {msg.content}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="flex justify-start">
            <div className="px-[14px] py-[9px] rounded-tl-[4px] rounded-tr-[14px] rounded-br-[14px] rounded-bl-[14px] bg-[#EAECEC] text-[#888] text-[13px] tracking-[2px]">
              ...
            </div>
          </div>
        )}
      </div>

      {/* Input — hidden when intake is complete */}
      {!complete && (
        <div className="border-t border-[#E2E5E5] px-3 py-[10px] flex gap-2 flex-shrink-0 bg-white">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer…"
            disabled={loading}
            rows={1}
            className="flex-1 resize-none border border-[#D1D5DB] rounded-md px-[11px] py-2 text-sm font-body leading-[1.45] text-silver-text outline-none bg-white disabled:bg-[#F5F5F5]"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="flex-shrink-0 px-[15px] text-sm font-bold text-white rounded-md transition-colors duration-150 bg-hunter disabled:bg-[#D1D5DB] disabled:cursor-default cursor-pointer"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
